
import { Component, Vue, Watch } from 'vue-property-decorator'
import type { Route } from 'vue-router'

import LoginForm from '@/components/forms/LoginForm.vue'
import { logInTrackJS } from '@/errors/helpers'
import { authExternal } from '@/helpers/AuthExternal'
import { isSameOriginPath, withSellerPrefix } from '@/helpers/route'
import { isLoggedIn } from '@/state/Authentication'
import store from '@/store/store'

function parseRedirectParam(param: string | string[] | undefined): string | null {
  if (typeof param !== 'string') {
    return null
  }

  if (!isSameOriginPath(param)) {
    logInTrackJS(`LoginRoute: Invalid return URL`, { returnPath: param })
    return null
  }

  // Ensure a leading slash so downstream consumers that concatenate a seller
  // prefix (e.g. Auth0Client.login, SFECClient.login) don't produce malformed
  // paths like `/seller1events/123`.
  return param.startsWith('/') ? param : `/${param}`
}

@Component({
  name: 'LoginRoute',
  components: { LoginForm },
})
export default class extends Vue {
  beforeRouteEnter(to: Route, from: Route, next: AnyFn): void {
    const { isExternalAuth, isLoggedInExternally, login } = authExternal
    const redirectPath = parseRedirectParam(to.query.redirect)

    if (isLoggedIn() || isLoggedInExternally()) {
      // Already logged in — redirect without mounting the component.
      // @see redirectAfterLogin()
      if (redirectPath) {
        window.location.href = withSellerPrefix(redirectPath)
      } else {
        next('/')
      }
    } else if (isExternalAuth()) {
      // External auth (Auth0, SFEC) — redirect to provider without mounting the component.
      login(redirectPath ?? '/')
    } else {
      next()
    }
  }

  get user(): unknown {
    return store.getters['Member/user']
  }

  // Same approach as in HomeRoute.
  @Watch('user')
  onLogin(value: unknown): void {
    if (value) {
      this.redirectAfterLogin()
    }
  }

  get redirectPath(): string | null {
    return parseRedirectParam(this.$route.query.redirect)
  }

  // @see beforeRouteEnter (already-logged-in case)
  redirectAfterLogin(): void {
    if (this.redirectPath) {
      // Use window.location instead of router.push because the return URL
      // may be for a different app (e.g., ecomm-next) whose routes don't
      // exist in web-client's Vue Router.
      window.location.href = withSellerPrefix(this.redirectPath)
    } else {
      this.$router.push('/')
    }
  }
}
